import React, { Component, Fragment, ReactElement } from 'react'
import { inject, observer } from 'mobx-react'
import {
  ApplyType,
  CreateOrEditDiscount,
  DiscountOption,
  DiscountProduct,
  DiscountCategory,
  productName,
} from '@getgreenline/homi-shared'
import { CollapseProps } from 'antd/lib/collapse'
import { SingleCollapsibleContainer } from '../../../../../components/ResponsiveContainer/SingleCollapsibleContainer'
import { ResponsiveRow } from '../../../../../components/ResponsiveContainer/ResponsiveRow'
import { Button, Icon, Popconfirm, Radio, Tooltip } from 'antd'
import { OptionProps } from 'antd/lib/select'
import { DiscountAllProducts } from './DiscountAllProducts'
import { DiscountSpecificProducts } from './DiscountSpecificProducts'
import { DiscountStore } from './DiscountStore'
import { DiscountCategories } from './DiscountCategories'
import { Link } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { isNull } from 'lodash'
import { Fade } from 'react-reveal'
import DepreciatedDiscountImage from '../../../../../images/img-discount-depreciated.png'
import { TApplyDiscount } from '../Discount.interface'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  discount: CreateOrEditDiscount
  discountStore: DiscountStore
  collapseProps: CollapseProps
  defaultFadeAnimationProps: ReactRevealProps
  currentTabType: ApplyType
  currentApplyTypeOption: TApplyDiscount
  setCurrentApplyTypeOption: (currentApplyTypeOption: TApplyDiscount) => void
  setDiscountTypeUndefined: (isDiscountUndefined: boolean) => void
}
interface State {
  discountType: TApplyDiscount
}

@inject('currentCompanyStore')
@observer
export class AddDiscountProducts extends Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)

    this.state = {
      discountType: this.props.discount.id ? this.appliedDiscountType : 'product',
    }
  }

  componentDidMount = () => {
    this.props.setDiscountTypeUndefined(!this.appliedDiscountType)
  }

  renderDiscountedProducts = (discountOption: DiscountOption, disabled?: boolean) => {
    const { discount, discountStore } = this.props
    const productData =
      discountOption === 'include' ? discountStore.includedProducts : discountStore.excludedProducts

    return productData.map((product, index) => {
      const parentProductName =
        (product.parentProductId &&
          this.props.discountStore.mappedParentProductName.get(product.parentProductId)) ||
        null

      const pName = productName({
        name: product.name,
        parentProductName,
      })

      return (
        <Fade {...this.props.defaultFadeAnimationProps} key={product.id}>
          <div className='discount-tag'>
            <Button
              style={{ width: 32, flex: 'none', marginRight: 8 }}
              icon='close'
              onClick={() => discount.removeProduct(index)}
              disabled={disabled}
            />

            <Link to={this.getProductPageLink(product)} target='_blank'>
              <span style={{ textDecoration: 'underline' }}>{pName || ''}</span>
            </Link>
          </div>
        </Fade>
      )
    })
  }

  renderDiscountedCategories = (discountOption: DiscountOption, disable?: boolean) => {
    const { discountStore } = this.props
    const categoryData =
      discountOption === 'include'
        ? discountStore.includedCategories
        : discountStore.excludedCategories

    return categoryData.map((category) => {
      const parentCategory = category.parentCategoryId
        ? discountStore.mappedFlattenedCategories.get(category.parentCategoryId)
        : undefined
      const childCategories = discountStore.getExcludedChildCategories(category)

      const categoryName = parentCategory
        ? `${parentCategory.name} - ${category.name}`
        : category.name

      const categoryColor = category.colorHex || 'gray'
      const categoryIconStyle = parentCategory
        ? { width: 12, height: 12 }
        : { width: 16, height: 16 }

      return (
        <Fade {...this.props.defaultFadeAnimationProps} key={category.id}>
          <div className='discount-tag'>
            {discountOption === 'include' && childCategories.length > 0 ? (
              <Popconfirm
                title='Excluded sub-categories will be removed'
                onConfirm={() => {
                  this.props.discountStore.removeDiscountCategory(category)

                  childCategories.forEach((childCategory) => {
                    this.props.discountStore.removeDiscountCategory(childCategory)
                  })
                }}
              >
                <Button
                  style={{ width: 32, flex: 'none', marginRight: 8 }}
                  icon='close'
                  disabled={disable}
                />
              </Popconfirm>
            ) : (
              <Button
                style={{ width: 32, flex: 'none', marginRight: 8 }}
                icon='close'
                onClick={() => this.props.discountStore.removeDiscountCategory(category)}
                disabled={disable}
              />
            )}
            <Link
              to={Routes.DASHBOARD_PRODUCTS_LISTING(
                this.company.id,
                `?selectedCategoryIds=${category.id}`,
              )}
              target='_blank'
            >
              <div className='d-flex align-items-center'>
                <span
                  style={{
                    ...categoryIconStyle,
                    backgroundColor: categoryColor,
                    marginRight: 8,
                    flex: 'none',
                  }}
                />

                <span style={{ textDecoration: 'underline' }}>{categoryName}</span>
              </div>
            </Link>
          </div>
        </Fade>
      )
    })
  }

  renderSelectField = (selectValue: TApplyDiscount) => {
    const { discountStore, currentApplyTypeOption } = this.props

    switch (true) {
      case selectValue === 'all' && currentApplyTypeOption === 'all':
        return (
          <Fade {...this.props.defaultFadeAnimationProps}>
            <div className='pt-1 pb-3'>
              <DiscountAllProducts discountStore={discountStore} filterOption={this.filterOption} />
            </div>
          </Fade>
        )
      case selectValue === 'product' && currentApplyTypeOption === 'product':
        return (
          <Fade {...this.props.defaultFadeAnimationProps}>
            <div className='pt-1 pb-3'>
              <DiscountSpecificProducts
                discountStore={discountStore}
                filterOption={this.filterOption}
              />
            </div>
          </Fade>
        )
      case selectValue === 'category' && currentApplyTypeOption === 'category':
        return (
          <Fade {...this.props.defaultFadeAnimationProps}>
            <div className='pt-1 pb-3'>
              <DiscountCategories
                discountStore={this.props.discountStore}
                filterOption={this.filterOption}
              />
            </div>
          </Fade>
        )
    }
  }

  renderSelectedItems = (
    discountOption: DiscountOption,
    renderItemType: 'products' | 'categories',
    disabled?: boolean,
  ) => {
    const { discount, discountStore } = this.props
    const isDiscountOptionInclude = discountOption === 'include'
    const itemDataSource = renderItemType === 'products' ? discount.products : discount.categories
    const productLength = isDiscountOptionInclude
      ? discountStore.includedProducts.length
      : discountStore.excludedProducts.length
    const categoryLength = isDiscountOptionInclude
      ? discountStore.includedCategories.length
      : discountStore.excludedCategories.length
    const labelText = isDiscountOptionInclude
      ? `Included ${renderItemType} (${
          renderItemType === 'products' ? productLength : categoryLength
        }):`
      : `Excluded ${renderItemType} (${
          renderItemType === 'products' ? productLength : categoryLength
        }):`

    const shouldItemBeRendered = itemDataSource.some((data: any) => {
      const item = data as DiscountProduct | DiscountCategory
      return item.type === discountOption
    })

    return (
      shouldItemBeRendered && (
        <div className='products-excluded'>
          <hr className='mt-2 mb-3' />

          <div className='caption mb-1'>
            <strong>{labelText}</strong>
          </div>

          <div className='discount-list'>
            {renderItemType === 'products'
              ? this.renderDiscountedProducts(discountOption, disabled)
              : this.renderDiscountedCategories(discountOption, disabled)}
          </div>
        </div>
      )
    )
  }

  getProductPageLink = (product: DiscountProduct) => {
    return isNull(product.parentProductId)
      ? Routes.EDIT_PRODUCT(this.company.id, product.id)
      : Routes.EDIT_PRODUCT(this.company.id, product.parentProductId)
  }

  filterOption = (input: string, option: ReactElement<OptionProps>) => {
    const optionName = option.props.children

    if (optionName && typeof optionName === 'string') {
      const lowerCaseInput = input.toLowerCase().trim()

      return optionName.toLowerCase().indexOf(lowerCaseInput) >= 0
    }

    return true
  }

  get isDiscountSet() {
    return this.props.discount.products.length > 0 || this.props.discount.categories.length > 0
  }

  get appliedDiscountType(): TApplyDiscount {
    const { discount } = this.props
    switch (true) {
      case discount.products.length === 0 && discount.categories.length === 0:
        return 'all'
      case discount.products.length === 0 &&
        discount.categories.every((category) => category.type === 'exclude'):
        return 'all'

      case discount.categories.length === 0 &&
        discount.products.every((product) => product.type === 'exclude'):
        return 'all'

      case discount.products.every((product) => product.type === 'exclude') &&
        discount.categories.every((category) => category.type === 'exclude'):
        return 'all'

      case discount.products.some((product) => product.type === 'include') &&
        discount.categories.some((category) => category.type === 'include'):
        return undefined

      case discount.categories.some((category) => category.type === 'include'):
        return 'category'

      case discount.products.length > 0 && discount.categories.length === 0:
        return 'product'

      default:
        return undefined
    }
  }

  get selectedDiscountItems() {
    const { discount, currentApplyTypeOption } = this.props

    switch (currentApplyTypeOption) {
      case 'all':
        return (
          <>
            {this.renderSelectedItems('exclude', 'products')}
            {this.renderSelectedItems('exclude', 'categories')}
          </>
        )
      case 'product':
        return this.renderSelectedItems('include', 'products')
      case 'category':
        return (
          <>
            {this.renderSelectedItems('include', 'categories')}

            {discount.categories.some((category) => category.type === 'exclude') && (
              <div className='products-excluded'>
                <hr className='mt-2 mb-3' />

                <div className='caption mb-1'>
                  <strong>
                    Excluded sub-categories ({this.props.discountStore.excludedCategories.length}):
                  </strong>
                </div>

                <div className='discount-list'>{this.renderDiscountedCategories('exclude')}</div>
              </div>
            )}

            {this.renderSelectedItems('exclude', 'products')}
          </>
        )
      default:
        return (
          <>
            <div className='helper mb-2'>
              <strong>Lists are read-only</strong>
            </div>

            {this.renderSelectedItems('include', 'products', true)}
            {this.renderSelectedItems('exclude', 'products', true)}
            {this.renderSelectedItems('include', 'categories', true)}
            {this.renderSelectedItems('exclude', 'categories', true)}
          </>
        )
    }
  }

  render() {
    const { discountType } = this.state
    const {
      discount,
      collapseProps,
      currentCompanyStore,
      currentTabType,
      currentApplyTypeOption,
      setCurrentApplyTypeOption,
    } = this.props

    const discountTypes: { value: TApplyDiscount; label: string }[] = [
      { value: 'product', label: 'Specific products' },
      { value: 'category', label: 'Specific categories' },
      { value: 'all', label: 'All products and categories' },
    ]

    const filteredDiscountTypes = discountTypes.filter((discountTypeOption) => {
      if (currentTabType === 'products') return discountTypeOption
      if (currentTabType === 'bundle') return discountTypeOption.value === 'product'
      if (currentTabType === 'cart')
        return ['category', 'all'].includes(discountTypeOption.value || '')
    })

    return (
      <SingleCollapsibleContainer
        collapseProps={collapseProps}
        panelProps={{ header: 'Products', key: 'products' }}
        defaultActive
      >
        <ResponsiveRow labelProps={{ className: 'mt-0' }} labelText={<strong>Applied to</strong>}>
          <Radio.Group
            value={currentApplyTypeOption}
            onChange={(e) => {
              if (!this.isDiscountSet) {
                setCurrentApplyTypeOption(e.target.value)
                this.props.setDiscountTypeUndefined(false)
              }
            }}
            style={{ width: '100%' }}
          >
            {filteredDiscountTypes.map((discountTypeOption) =>
              this.isDiscountSet ? (
                <Fragment key={discountTypeOption.value}>
                  <Popconfirm
                    title='This will remove all of the specified products and categories'
                    onConfirm={() => {
                      setCurrentApplyTypeOption(discountTypeOption.value)
                      discount.removeAllProducts()
                      discount.removeAllCategories()
                      this.props.setDiscountTypeUndefined(false)
                    }}
                    overlayClassName='gr-popover'
                  >
                    <Radio
                      value={discountTypeOption.value}
                      checked={discountType === discountTypeOption.value}
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      {discountTypeOption.label}
                    </Radio>
                  </Popconfirm>
                  {this.renderSelectField(discountTypeOption.value)}
                </Fragment>
              ) : (
                <Fragment key={discountTypeOption.value}>
                  <Radio
                    value={discountTypeOption.value}
                    checked={discountType === discountTypeOption.value}
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    {discountTypeOption.label}
                  </Radio>
                  {this.renderSelectField(discountTypeOption.value)}
                </Fragment>
              ),
            )}

            {!this.appliedDiscountType && (
              <Radio
                disabled
                key={'depreciated-discount-type'}
                value={undefined}
                checked={discountType === undefined}
                style={{ display: 'block', marginBottom: 4, whiteSpace: 'normal' }}
              >
                <Tooltip
                  overlayStyle={{ minWidth: 320 }}
                  title={
                    <div>
                      <p>Separate discounts for products and categories</p>
                      <img src={DepreciatedDiscountImage} className='w-100' />
                    </div>
                  }
                >
                  <span style={{ color: 'var(--red)' }}>
                    <Icon type='exclamation-circle' className='mr-2' />
                    Please set discounts for products and categories separately
                  </span>
                </Tooltip>
              </Radio>
            )}
          </Radio.Group>

          {this.selectedDiscountItems}
        </ResponsiveRow>
      </SingleCollapsibleContainer>
    )
  }
}
